import React, { useMemo } from "react";
import {
  Box,
  Button,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DownloadIcon from "@mui/icons-material/Download";
import {
  useMetricsUi,
  SectionHead,
  headCellSx,
  textCellSx,
} from "../../components/metrics/metricsUi";
import { formatDate, formatDateTime } from "../../utils/formatDate";
import { downloadSkuCoverageXlsx } from "../../utils/sku-coverage-xlsx-export";
import {
  hasWarehouseSplit,
  skippedWarehousesText,
  warehouseSplitLabel,
} from "../../utils/warehouse-split";

const n0 = (v) =>
  v == null || Number.isNaN(Number(v))
    ? "—"
    : Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 });
const n1 = (v) =>
  v == null || Number.isNaN(Number(v))
    ? "—"
    : Number(v).toLocaleString("en-US", { maximumFractionDigits: 1 });
const usd = (v) =>
  v == null || Number.isNaN(Number(v))
    ? "—"
    : `$${Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

const statusTone = (ui, status) => {
  const s = String(status || "").toLowerCase();
  if (/critical|heavily|stock ?out/.test(s)) return ui.danger;
  if (/low|over/.test(s)) return ui.warning;
  if (/healthy|good|balanced/.test(s)) return ui.positive;
  return ui.accent;
};
const levelTone = (ui, level) => {
  const l = String(level || "").toLowerCase();
  return l === "high" ? ui.danger : l === "low" ? ui.positive : ui.warning;
};

const Badge = ({ color, label }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      fontSize: 11,
      fontWeight: 700,
      color,
      backgroundColor: `${color}1A`,
      borderRadius: "20px",
      px: 1.1,
      py: 0.25,
      whiteSpace: "nowrap",
    }}
  >
    {label}
  </Box>
);

const NoteCard = ({ ui, tone, title, detail }) => (
  <Box
    sx={{
      ...ui.card,
      borderLeft: `3px solid ${tone}`,
      p: "10px 14px",
      mb: 1,
      textAlign: "left",
    }}
  >
    <Typography
      sx={{
        fontSize: 13,
        fontWeight: 700,
        color: ui.colors.textPrimary,
        textAlign: "left",
      }}
    >
      {title}
    </Typography>
    <Typography
      sx={{
        fontSize: 12.5,
        color: ui.colors.textSecondary,
        mt: 0.25,
        textAlign: "left",
      }}
    >
      {detail}
    </Typography>
  </Box>
);

const TableSkeleton = ({ ui, rows = 4 }) => (
  <TableContainer sx={{ ...ui.card, p: 0 }}>
    <Table size="small">
      <TableBody>
        {Array.from({ length: rows }).map((_, i) => (
          <TableRow key={i}>
            {Array.from({ length: 6 }).map((__, j) => (
              <TableCell key={j} sx={textCellSx(ui)}>
                <Skeleton
                  variant="text"
                  width={j === 0 ? "70%" : "50%"}
                  animation="wave"
                />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const NotesSkeleton = ({ ui, count = 3 }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <Box key={i} sx={{ ...ui.card, p: "10px 14px", mb: 1 }}>
        <Skeleton variant="text" width="45%" animation="wave" />
        <Skeleton variant="text" width="85%" animation="wave" />
      </Box>
    ))}
  </>
);

const regionLabel = (m) => `${m.zip3}xx${m.state ? ` · ${m.state}` : ""}`;

/**
 * Where the product's forecast should be stocked: the split per warehouse
 * (summed across SKUs) and the largest delivery regions behind it, with the
 * warehouse each one was routed to and its shipping zone.
 */
const ArtifactAllocation = ({ ui, data }) => {
  const { colors } = ui;
  const totals = data?.totals?.warehouse_allocation || [];
  const info = data?.warehouse_allocation;
  if (!totals.length || !info) return null;
  const w = info.order_window || {};
  const regions = info.zone_mapping || [];
  const note = { fontSize: 12.5, color: colors.textSecondary, lineHeight: 1.6, textAlign: "left" };
  return (
    <Box>
      <SectionHead title="Warehouse Allocation" />
      <Box sx={{ ...ui.card, p: "12px 16px", mb: 1.25 }}>
        <Typography sx={note}>
          The forecast was run on all orders, then split by where demand comes from:
          each delivery region was assigned to the active warehouse with the lowest
          USPS shipping zone
          {w.months ? `, using orders from the last ${w.months} months` : ""}
          {w.from && w.to ? ` (${formatDate(w.from)} – ${formatDate(w.to)})` : ""}.
        </Typography>
        {info.skipped_warehouses?.length > 0 && (
          <Typography sx={note}>
            Not included: {skippedWarehousesText(info.skipped_warehouses)}.
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
        }}
      >
        <TableContainer sx={{ ...ui.card, p: 0, maxHeight: 320 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx(ui)}>Warehouse</TableCell>
                <TableCell sx={headCellSx(ui)}>Share</TableCell>
                <TableCell sx={headCellSx(ui)}>Avg Zone</TableCell>
                <TableCell sx={headCellSx(ui)}>Allocated Units</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {totals.map((t) => (
                <TableRow key={String(t.warehouse)}>
                  <TableCell sx={textCellSx(ui)}>
                    <strong>{t.name || "Warehouse"}</strong>
                  </TableCell>
                  <TableCell sx={textCellSx(ui)}>{n1(t.pct * 100)}%</TableCell>
                  <TableCell sx={textCellSx(ui)}>
                    {t.avg_zone == null ? "—" : n1(t.avg_zone)}
                  </TableCell>
                  <TableCell sx={textCellSx(ui)}>{n0(t.forecast_units)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {regions.length > 0 && (
          <TableContainer sx={{ ...ui.card, p: 0, maxHeight: 320 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx(ui)}>Top Delivery Regions</TableCell>
                  <TableCell sx={headCellSx(ui)}>Order Units</TableCell>
                  <TableCell sx={headCellSx(ui)}>Nearest Warehouse</TableCell>
                  <TableCell sx={headCellSx(ui)}>Zone</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {regions.map((m) => (
                  <TableRow key={m.zip3}>
                    <TableCell sx={textCellSx(ui)}>{regionLabel(m)}</TableCell>
                    <TableCell sx={textCellSx(ui)}>{n0(m.units)}</TableCell>
                    <TableCell sx={textCellSx(ui)}>
                      {(m.warehouses || []).join(" / ") || "—"}
                    </TableCell>
                    <TableCell sx={textCellSx(ui)}>{m.zone ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
};

/**
 * "Product Artifact" sections — SKU Detail & Coverage table, then Key
 * Issues / Recommendations side by side (matching the standalone HTML
 * report's two-column layout) — for the product currently on screen.
 * Generated server-side whenever a Product-scoped forecast runs for it (see
 * forecast.class.js#triggerProductArtifacts).
 *
 * Pure presentational — `artifact`/`generate` come from a single shared
 * useForecastArtifact instance owned by ForecastResults, so this and
 * ProductArtifactInsight (the summary/insight block placed before the
 * Forecasted Units chart) always agree on state without either causing a
 * second fetch/subscribe/auto-generate.
 */
const ProductArtifactSections = ({ artifact, generate }) => {
  const ui = useMetricsUi();
  const { colors } = ui;

  const bySku = useMemo(
    () => new Map((artifact.narrative?.skus || []).map((s) => [s.sku, s])),
    [artifact.narrative],
  );

  if (artifact.status === "loading" || artifact.status === "pending") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box>
          <SectionHead title="SKU Detail & Coverage" />
          <TableSkeleton ui={ui} />
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
          }}
        >
          <Box>
            <SectionHead title="Key Observations" />
            <NotesSkeleton ui={ui} count={2} />
          </Box>
          <Box>
            <SectionHead title="Recommendations" />
            <NotesSkeleton ui={ui} count={2} />
          </Box>
        </Box>
        <Typography
          sx={{ fontSize: 12, color: colors.textSecondary, textAlign: "left" }}
        >
          {artifact.status === "pending"
            ? "Generating the product analysis — progress is shown at the top of the page."
            : "Loading the product analysis…"}
        </Typography>
      </Box>
    );
  }

  if (artifact.status === "none" || artifact.status === "error") {
    return (
      <Box
        sx={{
          ...ui.card,
          p: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography
            sx={{ fontSize: 13, fontWeight: 700, color: colors.textPrimary }}
          >
            {artifact.status === "error"
              ? "Couldn't generate the product analysis"
              : "No product analysis yet"}
          </Typography>
          {artifact.error && (
            <Typography
              sx={{ fontSize: 12, color: colors.textSecondary, mt: 0.25 }}
            >
              {artifact.error}
            </Typography>
          )}
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={
            artifact.status === "error" ? <RefreshIcon /> : <AutoAwesomeIcon />
          }
          onClick={generate}
        >
          {artifact.status === "error" ? "Retry" : "Generate"}
        </Button>
      </Box>
    );
  }

  // status === "ready"
  const skus = artifact.data?.skus || [];
  const narrative = artifact.narrative || {};
  const showSplit = hasWarehouseSplit(skus);

  const downloadExcel = () => {
    const productName = artifact.data?.product?.name || "product";
    downloadSkuCoverageXlsx(
      artifact.data,
      narrative,
      `${productName.replace(/[^\w-]+/g, "_")}_sku_coverage.xlsx`,
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box
        sx={{
          ...ui.card,
          p: "10px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        {artifact.generated_at ? (
          <Typography
            sx={{
              fontSize: 11,
              color: colors.textSecondary,
              textAlign: "left",
            }}
          >
            Generated {formatDateTime(artifact.generated_at)}
          </Typography>
        ) : (
          <span />
        )}
        <Button
          size="small"
          startIcon={<RefreshIcon />}
          onClick={generate}
          sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
        >
          Regenerate
        </Button>
      </Box>

      <Box>
        <SectionHead
          title="SKU Detail & Coverage"
          right={
            skus.length > 0 && (
              <Button
                size="small"
                startIcon={<DownloadIcon />}
                onClick={downloadExcel}
              >
                Download Excel
              </Button>
            )
          }
        />
        <TableContainer sx={{ ...ui.card, p: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx(ui)}>SKU</TableCell>
                <TableCell sx={headCellSx(ui)}>Inventory</TableCell>
                <TableCell sx={headCellSx(ui)}>Total Demand</TableCell>
                <TableCell sx={headCellSx(ui)}>Avg Mo. Demand</TableCell>
                <TableCell sx={headCellSx(ui)}>Mo. Coverage</TableCell>
                <TableCell sx={headCellSx(ui)}>Status</TableCell>
                <TableCell sx={headCellSx(ui)}>Capital at Cost</TableCell>
                {showSplit && (
                  <TableCell sx={headCellSx(ui)}>Warehouse Split</TableCell>
                )}
                <TableCell sx={headCellSx(ui)}>Recommendation</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {skus.map((s) => {
                const nar = bySku.get(s.sku) || {};
                return (
                  <TableRow key={s.sku}>
                    <TableCell sx={textCellSx(ui)}>
                      <strong>{s.sku}</strong>
                      <Typography
                        sx={{ fontSize: 10.5, color: colors.textSecondary }}
                      >
                        {s.label}
                      </Typography>
                    </TableCell>
                    <TableCell sx={textCellSx(ui)}>
                      {n0(s.inventory?.on_hand)}
                    </TableCell>
                    <TableCell sx={textCellSx(ui)}>
                      {n0(s.demand?.total)}
                    </TableCell>
                    <TableCell sx={textCellSx(ui)}>
                      {n1(s.demand?.avg_monthly)}
                    </TableCell>
                    <TableCell sx={textCellSx(ui)}>
                      {s.coverage_months == null ? "—" : n1(s.coverage_months)}
                    </TableCell>
                    <TableCell sx={textCellSx(ui)}>
                      {nar.status ? (
                        <Badge
                          color={statusTone(ui, nar.status)}
                          label={nar.status}
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell sx={textCellSx(ui)}>
                      {usd(s.capital_at_cost)}
                    </TableCell>
                    {showSplit && (
                      <TableCell sx={textCellSx(ui)}>
                        {warehouseSplitLabel(s.warehouse_allocation) || "—"}
                      </TableCell>
                    )}
                    <TableCell sx={textCellSx(ui)}>
                      {nar.recommendation || "—"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <ArtifactAllocation ui={ui} data={artifact.data} />

      {/* Key Issues / Recommendations side by side, same as the standalone
          HTML report's two-column layout. */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
        }}
      >
        <Box>
          <SectionHead title="Key Observations" />
          {(narrative.key_issues || []).length ? (
            narrative.key_issues.map((issue, i) => (
              <NoteCard
                key={i}
                ui={ui}
                tone={levelTone(ui, issue.severity)}
                title={issue.title}
                detail={issue.detail}
              />
            ))
          ) : (
            <Typography
              sx={{
                fontSize: 12.5,
                color: colors.textSecondary,
                textAlign: "left",
              }}
            >
              None reported.
            </Typography>
          )}
        </Box>

        <Box>
          <SectionHead title="Recommendations" />
          {(narrative.recommendations || []).length ? (
            narrative.recommendations.map((rec, i) => (
              <NoteCard
                key={i}
                ui={ui}
                tone={levelTone(ui, rec.priority)}
                title={rec.title}
                detail={rec.detail}
              />
            ))
          ) : (
            <Typography
              sx={{
                fontSize: 12.5,
                color: colors.textSecondary,
                textAlign: "left",
              }}
            >
              None reported.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ProductArtifactSections;
